// UnreadsView — All Unreads activity view (#1866).
//
// One pane, every unread message across every joined room, grouped by
// room. Header carries the aggregate count and
// "Mark all read"; each group is a 40px room header with a count chip,
// followed by compact 40px message rows. Click jumps to the source
// room at the message; Mark all read fires a bulk read receipt per
// group.
//
// The view reads `useUnreads()` for the digest and `useMatrix()` for
// `jumpToEvent`. Tokens-only (`--chat-*`); the inline pixel values
// allowed are the brief-pinned dimensions called out in the ticket:
//   - 40px row + room-header height
//   - 18×18 count chip
//   - 80×80 empty illustration
//   - 24×24 compact-DM avatar with `fontSize: 11` initials, matching
//     the existing `Sidebar.tsx` workspace-avatar precedent (the
//     digest is a triage surface, not the room timeline; the smaller
//     avatar mirrors the compact density the ticket asks for).

import { useState, type CSSProperties, type ReactNode } from "react";
import { Hash, Inbox } from "lucide-react";
import { RelativeLabel } from "@nurion/ui/relative-label";

import { ConfirmDialog } from "~/components/ConfirmDialog";
import { EmptyState } from "~/components/EmptyState";
import { useMatrix } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import { useUnreads } from "~/hooks/useUnreads";
import { getInitials } from "~/lib/initials";
import { replacementBody } from "~/lib/message-utils";
import type { TimelineEvent, UnreadGroup } from "~/lib/matrix";
import { absoluteFallback, LOCAL_ZONE } from "~/lib/relative-time";
import { ICON_STROKE } from "~/lib/icon";

/** Hover background + remove-on-loading-pulse keyframes for the
 *  message rows. Injected once on module load — same pattern the
 *  SavedView uses, kept here so the component is self-contained. */
const ROW_STYLE_ID = "chat-unreads-row-styles";
function injectRowStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(ROW_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = ROW_STYLE_ID;
  style.textContent = `
    .chat-unreads-row:hover {
      background: var(--chat-surface-raised);
    }
    @keyframes chat-unreads-shimmer-pulse {
      0%, 100% { background-color: var(--chat-surface-raised); }
      50% { background-color: var(--chat-surface-overlay); }
    }
    .chat-unreads-shimmer {
      animation: chat-unreads-shimmer-pulse var(--chat-motion-slow) var(--chat-motion-ease) infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .chat-unreads-shimmer { animation: none; }
    }
  `;
  document.head.appendChild(style);
}
injectRowStyles();

/* ─── view ─────────────────────────────────────────────────────── */

export function UnreadsView() {
  const { groups, totalUnread, markAllRead } = useUnreads();
  const { jumpToEvent, state } = useMatrix();
  const { push: pushToast } = useToast();
  const [confirmingMark, setConfirmingMark] = useState(false);
  const [marking, setMarking] = useState(false);

  const handleMarkAll = async () => {
    setConfirmingMark(false);
    setMarking(true);
    // `markAllRead` awaits every `sendReadReceipt` via `Promise.all`,
    // and `sendReadReceipt` itself swallows binding errors — so the
    // await cannot reject. The busy state is surfaced through
    // `marking`; the live subscription clears the digest as the
    // server acks. A failed receipt is best-effort and retries on
    // the user's next glance at the room.
    await markAllRead();
    setMarking(false);
    pushToast({ message: "Marked everything read." });
  };

  // Loading shimmer — only while the client state machine is still
  // walking through `loading` / `syncing`. The shell only mounts
  // this view once `state === "synced"`, so in production the
  // shimmer is unreachable; the story-local provider drives the
  // variant by passing `state: "syncing"`.
  if (state === "loading" || state === "syncing") {
    return (
      <ViewShell title="Unreads">
        <UnreadsShimmer />
      </ViewShell>
    );
  }

  const action =
    groups.length > 0
      ? {
          label: marking ? "Marking…" : "Mark all read",
          onClick: () => setConfirmingMark(true),
          disabled: marking,
        }
      : null;

  return (
    <ViewShell title="Unreads" count={groups.length > 0 ? totalUnread : undefined} action={action}>
      {groups.length === 0 ? (
        <EmptyState
          icon={<CaughtUpIllustration />}
          primary="You're all caught up."
          secondary="No unread messages."
        />
      ) : (
        <div
          style={{
            flex: 1,
            overflowY: "auto",
          }}
        >
          {groups.map((group) => (
            <UnreadGroupSection
              key={group.roomId}
              group={group}
              onJump={(eventId) => jumpToEvent({ roomId: group.roomId, eventId })}
            />
          ))}
        </div>
      )}
      {confirmingMark && (
        <ConfirmDialog
          title="Mark all unreads read?"
          body="This sends a read receipt for the latest message in every room with unreads."
          confirmLabel="Mark all read"
          onConfirm={() => void handleMarkAll()}
          onCancel={() => setConfirmingMark(false)}
        />
      )}
    </ViewShell>
  );
}

/* ─── shell + header ─────────────────────────────────────────── */

function ViewShell({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: { label: string; onClick: () => void; disabled?: boolean } | null;
  children: ReactNode;
}) {
  return (
    <section
      data-testid="unreads-view"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        height: "100%",
        background: "var(--chat-surface-app)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <Header title={title} count={count} action={action ?? null} />
      {children}
    </section>
  );
}

function Header({
  title,
  count,
  action,
}: {
  title: string;
  count?: number;
  action: { label: string; onClick: () => void; disabled?: boolean } | null;
}) {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: "var(--chat-timeline-room-header-height)",
        padding: "0 var(--chat-space-4)",
        borderBottom: "1px solid var(--chat-surface-border)",
        flexShrink: 0,
      }}
    >
      <Inbox
        size={16}
        strokeWidth={ICON_STROKE}
        style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
      />
      <h2
        style={{
          flex: 1,
          margin: 0,
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-display)",
          fontSize: "var(--chat-type-display-sm-size)",
          lineHeight: "var(--chat-type-display-sm-line)",
          fontWeight: 300,
        }}
      >
        {title}
        {count !== undefined && (
          <span
            style={{
              marginLeft: "var(--chat-space-2)",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              fontWeight: 400,
            }}
          >
            · {count}
          </span>
        )}
      </h2>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          disabled={action.disabled === true}
          className="chat-button"
          style={ghostButtonStyle}
        >
          {action.label}
        </button>
      )}
    </header>
  );
}

/* ─── group ─────────────────────────────────────────────────── */

function UnreadGroupSection({
  group,
  onJump,
}: {
  group: UnreadGroup;
  onJump: (eventId: string) => void;
}) {
  return (
    <section
      aria-label={`Unreads in ${group.roomName}`}
      style={{
        display: "flex",
        flexDirection: "column",
        borderBottom: "1px solid var(--chat-surface-border)",
      }}
    >
      <GroupHeader group={group} />
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {group.events.map((event) => (
          <li key={event.id}>
            <UnreadRow event={event} onJump={() => onJump(event.id)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function GroupHeader({ group }: { group: UnreadGroup }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        // 40px is the brief-pinned room-header height for this view.
        // No `--chat-row-*` token matches; inline keeps the dimension
        // close to the only consumer.
        height: 40,
        padding: "0 var(--chat-space-4)",
        background: "var(--chat-surface-raised)",
      }}
    >
      <RoomGlyph isDirect={group.isDirect} name={group.roomName} />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {group.roomName}
      </span>
      <CountChip count={group.unreadCount} highlight={group.highlightCount > 0} />
    </div>
  );
}

function RoomGlyph({ isDirect, name }: { isDirect: boolean; name: string }) {
  if (isDirect) {
    const initials = getInitials(name) || "–";
    return (
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: "var(--chat-radius-full)",
          background: "var(--chat-surface-raised)",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: 11,
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {initials}
      </span>
    );
  }
  return (
    <Hash
      aria-hidden
      size={14}
      strokeWidth={ICON_STROKE}
      style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
    />
  );
}

function CountChip({ count, highlight }: { count: number; highlight: boolean }) {
  return (
    <span
      style={{
        // 18×18 chip pinned by the brief.
        minWidth: 18,
        height: 18,
        padding: "0 var(--chat-space-1)",
        borderRadius: "var(--chat-radius-full)",
        background: highlight ? "var(--chat-cta-bg)" : "var(--chat-unread-dot)",
        color: highlight ? "var(--chat-cta-fg)" : "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
        lineHeight: "var(--chat-type-body-xs-line)",
        fontWeight: 400,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {count}
    </span>
  );
}

/* ─── row ───────────────────────────────────────────────────── */

function UnreadRow({ event, onJump }: { event: TimelineEvent; onJump: () => void }) {
  const senderName = event.sender.displayName || event.sender.id;
  const body = previewBody(event);
  return (
    <button
      type="button"
      onClick={onJump}
      className="chat-unreads-row"
      aria-label={`Jump to ${senderName}: ${body}`}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        // 40px row pinned by the brief. The compact density mirrors
        // what the digest is for — fast triage rather than reading.
        height: 40,
        width: "100%",
        padding: "0 var(--chat-space-4)",
        border: "none",
        background: "transparent",
        color: "inherit",
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          fontWeight: 400,
          flexShrink: 0,
          maxWidth: "30%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {senderName}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {body}
      </span>
      <span
        style={{
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
          flexShrink: 0,
        }}
      >
        <RelativeLabel
          value={new Date(event.ts).toISOString()}
          zone={LOCAL_ZONE}
          fallback={absoluteFallback(event.ts)}
        />
      </span>
    </button>
  );
}

/** Single-line body preview for the row. We deliberately avoid
 *  parsing inline markdown here — the digest is fast triage; full
 *  rendering belongs in the timeline. State events shouldn't reach
 *  this row (filtered upstream), so the default branch is just a
 *  defensive fallback. */
function previewBody(event: TimelineEvent): string {
  switch (event.content.type) {
    case "m.text":
    case "m.notice":
      return replacementBody(event.content);
    case "m.emote":
      return `* ${event.sender.displayName || event.sender.id} ${replacementBody(event.content)}`;
    case "m.image":
      return replacementBody(event.content) || "Image";
    case "m.video":
      return replacementBody(event.content) || "Video";
    case "m.file":
      return replacementBody(event.content) || "File";
    default:
      return "Message";
  }
}

/* ─── empty + loading ───────────────────────────────────────── */

/** 80×80 illustration from the ticket — quiet inbox with a check.
 *  Inline SVG so a missing asset can't strand the empty state and
 *  so it inherits the chat foreground tokens. */
function CaughtUpIllustration() {
  return (
    <svg
      aria-hidden
      width={80}
      height={80}
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx={40}
        cy={40}
        r={32}
        stroke="var(--chat-surface-raised)"
        strokeWidth={ICON_STROKE}
        fill="var(--chat-surface-panel)"
      />
      <path
        d="M28 42 l8 8 l16 -18"
        stroke="var(--chat-fg-secondary)"
        strokeWidth={ICON_STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function UnreadsShimmer() {
  // Five rows pinned by the ticket. Mirrors the room-list shimmer
  // shape so the user doesn't see the list visibly jump once real
  // data lands.
  return (
    <div
      aria-hidden
      style={{
        flex: 1,
        padding: "var(--chat-space-2)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-1)",
      }}
    >
      {[0, 1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className="chat-unreads-shimmer"
          style={{
            height: 40,
            width: "100%",
            borderRadius: "var(--chat-radius-sm)",
          }}
        />
      ))}
    </div>
  );
}

/* ─── styles ───────────────────────────────────────────────── */

const ghostButtonStyle: CSSProperties = {
  padding: "var(--chat-space-1) var(--chat-space-3)",
  borderRadius: "var(--chat-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  cursor: "pointer",
};
